import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { companiesAPI, employeesAPI, hrDocsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Loader2, FileText, Download, Eye, History } from 'lucide-react';

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const OfferLetterGenerator = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({
    company_id: '', employee_id: '', candidate_name: '', candidate_email: '',
    position: '', department: '', joining_date: '', salary: '', currency: 'AED',
    reporting_manager: '', work_location: '', probation_period: '6 months', remarks: '',
  });
  const [preview, setPreview] = useState(null);

  const allowedRole = !user || ['super_admin', 'hr_admin'].includes(user?.role);

  const fetchAll = async () => {
    try {
      const [c, e, h] = await Promise.all([companiesAPI.getAll(), employeesAPI.getAll(), hrDocsAPI.list({ document_type: 'offer_letter' })]);
      setCompanies(c.data); setEmployees(e.data); setHistory(h.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (allowedRole) fetchAll(); /* eslint-disable-next-line */ }, [allowedRole]);

  const onPickEmployee = (id) => {
    const emp = employees.find(e => e.id === id);
    if (emp) {
      setForm({
        ...form,
        employee_id: id,
        candidate_name: `${emp.first_name} ${emp.last_name}`,
        candidate_email: emp.email || '',
        company_id: emp.company_id || form.company_id,
        department: emp.department || form.department,
        position: emp.designation || form.position,
      });
    } else {
      setForm({ ...form, employee_id: '' });
    }
  };

  const generate = async (download) => {
    if (!form.company_id || !form.candidate_name || !form.position || !form.joining_date || !form.salary) {
      toast.error('Company, candidate, position, joining date and salary are required');
      return;
    }
    setGenerating(true);
    try {
      const payload = { ...form, salary: parseFloat(form.salary), employee_id: form.employee_id || null };
      const r = await hrDocsAPI.generateOfferLetter(payload);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (download) downloadBlob(blob, `offer_letter_${form.candidate_name.replace(/ /g, '_')}.pdf`);
      else setPreview(url);
      toast.success('Generated successfully');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const downloadHistory = async (id, filename) => {
    try {
      const r = await hrDocsAPI.download(id);
      downloadBlob(new Blob([r.data], { type: 'application/pdf' }), filename);
    } catch (e) { toast.error('Download failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!allowedRole) return <Navigate to="/" replace />;

  return (
    <div className="space-y-6" data-testid="offer-letter-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><FileText />Offer Letter Generator</h1>
        <p className="text-muted-foreground">Generate branded offer letters using the company's letterhead</p>
      </div>

      <Tabs defaultValue="generate">
        <TabsList>
          <TabsTrigger value="generate" data-testid="tab-generate">Generate</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="generate">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Company *</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({...form, company_id: v})}>
                    <SelectTrigger data-testid="ol-company"><SelectValue placeholder="Select company" /></SelectTrigger>
                    <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.country})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Existing Employee (optional)</Label>
                  <Select value={form.employee_id || 'none'} onValueChange={(v) => onPickEmployee(v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="ol-employee"><SelectValue placeholder="New candidate" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— New candidate —</SelectItem>
                      {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Candidate Name *</Label><Input value={form.candidate_name} onChange={(e) => setForm({...form, candidate_name: e.target.value})} data-testid="ol-name" /></div>
                  <div><Label>Email</Label><Input type="email" value={form.candidate_email} onChange={(e) => setForm({...form, candidate_email: e.target.value})} data-testid="ol-email" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Position *</Label><Input value={form.position} onChange={(e) => setForm({...form, position: e.target.value})} data-testid="ol-position" /></div>
                  <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} data-testid="ol-dept" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Joining Date *</Label><Input type="date" value={form.joining_date} onChange={(e) => setForm({...form, joining_date: e.target.value})} data-testid="ol-join" /></div>
                  <div><Label>Probation Period</Label><Input value={form.probation_period} onChange={(e) => setForm({...form, probation_period: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2"><Label>Annual Salary *</Label><Input type="number" value={form.salary} onChange={(e) => setForm({...form, salary: e.target.value})} data-testid="ol-salary" /></div>
                  <div><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Reporting Manager</Label><Input value={form.reporting_manager} onChange={(e) => setForm({...form, reporting_manager: e.target.value})} /></div>
                  <div><Label>Work Location</Label><Input value={form.work_location} onChange={(e) => setForm({...form, work_location: e.target.value})} /></div>
                </div>
                <div><Label>Remarks (optional)</Label><Textarea value={form.remarks} onChange={(e) => setForm({...form, remarks: e.target.value})} rows={2} /></div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => generate(false)} disabled={generating} data-testid="ol-preview-btn">
                    {generating ? <Loader2 size={14} className="animate-spin mr-2" /> : <Eye size={14} className="mr-2" />}Preview
                  </Button>
                  <Button onClick={() => generate(true)} disabled={generating} data-testid="ol-download-btn">
                    {generating ? <Loader2 size={14} className="animate-spin mr-2" /> : <Download size={14} className="mr-2" />}Download PDF
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Preview</CardTitle></CardHeader>
              <CardContent>
                {preview ? (
                  <iframe src={preview} className="w-full" style={{ height: 600 }} title="preview" data-testid="ol-preview-iframe" />
                ) : (
                  <div className="border-2 border-dashed rounded p-12 text-center text-muted-foreground">
                    Click <strong>Preview</strong> to see the generated PDF here, or <strong>Download PDF</strong> to save it directly.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Candidate / Employee</TableHead><TableHead>Position</TableHead>
                <TableHead>Salary</TableHead><TableHead>Generated</TableHead>
                <TableHead>By</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {history.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No offer letters generated yet</TableCell></TableRow>}
                {history.map(d => (
                  <TableRow key={d.id} data-testid={`history-row-${d.id}`}>
                    <TableCell className="font-medium">{d.template_data?.candidate_name || d.employee_name || '—'}</TableCell>
                    <TableCell>{d.template_data?.position || '—'}</TableCell>
                    <TableCell>{d.template_data?.currency} {Number(d.template_data?.salary || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-xs">{new Date(d.generated_at).toLocaleString()}</TableCell>
                    <TableCell className="text-xs">{d.generated_by}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => downloadHistory(d.id, d.filename)} data-testid={`download-${d.id}`}>
                        <Download size={12} className="mr-1" />PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OfferLetterGenerator;
